import { useState } from "react";
import { articleApi, type ArticleUnitSummary } from "../api/articleUnits";
import { ApiError } from "../api/types";
import { Button, TextField } from "../compose/controls";
import { Dialog } from "../compose/Dialog";
import { PenLine } from "../compose/Icon";
import { isValidSlug, slugify } from "../lib/slug";

/**
 * Rename an article project — `PATCH /api/article-units/:id`.
 *
 * ── WHAT THE SERVER DOES WITH THIS, AND WHY THE FORM IS SHAPED THIS WAY ─────
 * The PATCH body is ABSENT-means-unchanged: a request that names only `title`
 * must not blank the brand. So this dialog sends ONLY the fields the user
 * actually changed — never the whole form back — and `handlers/article_units.zig`
 * refuses a body that names nothing (400 `nothing_to_change`), which is why the
 * submit is disabled while nothing differs rather than round-tripping a refusal.
 *
 * THE SLUG IS NOT DERIVED FROM THE TITLE HERE, deliberately. In
 * `<NewArticleDialog>` the slug follows the title because neither exists yet; on
 * an EXISTING project the slug is a live URL, and silently re-slugging a project
 * because someone fixed a typo in its title breaks every link anyone shared. The
 * title-derived slug is offered as a one-click suggestion and never applied on
 * its own.
 *
 * A duplicate slug is a 409 `slug_taken`, and the SERVER's own sentence is
 * rendered INLINE next to the form (not as a toast): the message names the fix
 * ("choose a different slug"), and a toast that disappears while the user is
 * still looking at the field it refers to is not an error message.
 */
export function RenameArticleDialog({
  unit,
  targetWords,
  onClose,
  onRenamed,
}: {
  unit: ArticleUnitSummary;
  /** The project's stored word target, when the caller knows it (the library row
   *  does not carry it — only `GET /api/article-units/:id` does). Absent ⇒ the
   *  field starts empty and is only sent if the user types one. */
  targetWords?: number;
  onClose: () => void;
  onRenamed: (unit: ArticleUnitSummary) => void;
}) {
  const [title, setTitle] = useState(unit.title);
  const [slug, setSlug] = useState(unit.slug);
  const [brand, setBrand] = useState(unit.brand);
  const [lang, setLang] = useState(unit.lang);
  const [words, setWords] = useState(
    targetWords != null ? String(targetWords) : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedTitle = title.trim();
  const trimmedSlug = slug.trim();
  /** What the slug WOULD be if derived from the title as it stands — the live
   *  preview. Shown, never silently applied. */
  const suggested = slugify(trimmedTitle);
  const wordsNum = words.trim() === "" ? null : Number(words.trim());
  const originalWords = targetWords ?? null;

  const changed =
    trimmedTitle !== unit.title ||
    trimmedSlug !== unit.slug ||
    brand !== unit.brand ||
    lang !== unit.lang ||
    wordsNum !== originalWords;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (trimmedTitle.length === 0) {
      setError("Give the article a title.");
      return;
    }
    if (!isValidSlug(trimmedSlug)) {
      setError(
        trimmedSlug.length === 0
          ? "A project needs a slug — type one, or use the suggestion."
          : "Slug must be lowercase letters, numbers and hyphens, and cannot start or end with a hyphen.",
      );
      return;
    }
    if (words.trim() !== "" && (!Number.isFinite(wordsNum) || (wordsNum ?? 0) < 0)) {
      setError("The word target must be a whole number, or empty.");
      return;
    }

    // ONLY what changed. Every key here is a declared `PatchBody` field —
    // `articleAdmin.conformance.test.ts` reads the Zig struct and fails the build
    // on any name this dialog invents.
    const patch: {
      title?: string;
      slug?: string;
      brand?: string;
      lang?: string;
      target_words?: number;
      clear_target_words?: boolean;
    } = {};
    if (trimmedTitle !== unit.title) patch.title = trimmedTitle;
    if (trimmedSlug !== unit.slug) patch.slug = trimmedSlug;
    if (brand !== unit.brand) patch.brand = brand;
    if (lang !== unit.lang) patch.lang = lang;
    if (wordsNum !== originalWords) {
      // "The user emptied the word-target box" and "the user did not touch it"
      // are different requests, and one optional cannot say both — hence the
      // separate `clear_target_words` flag the server declares for exactly this.
      if (wordsNum == null) patch.clear_target_words = true;
      else patch.target_words = wordsNum;
    }

    setBusy(true);
    try {
      const updated = await articleApi.renameArticleUnit(unit.id, patch);
      setBusy(false);
      onRenamed(updated);
    } catch (err) {
      setBusy(false);
      // The server's own sentence wins — it knows which slug is taken and what
      // to do instead. Only a transport failure gets our words.
      setError(
        err instanceof ApiError && err.message
          ? err.message
          : "Could not rename the project.",
      );
    }
  }

  return (
    <Dialog
      title="Rename this article project"
      subtitle="Only what you change is sent — everything else keeps its stored value."
      onClose={onClose}
      testid="rename-article-dialog"
    >
      <form onSubmit={submit}>
        {error ? (
          <div className="auth__error" role="alert" data-testid="ra-error">
            {error}
          </div>
        ) : null}

        <TextField
          label="Title"
          value={title}
          data-testid="ra-title"
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />

        <TextField
          label="Slug"
          hint="Used in links to this project. Changing it breaks links people already have."
          value={slug}
          data-testid="ra-slug"
          onChange={(e) => setSlug(e.target.value)}
        />
        <p className="field__hint" data-testid="ra-slug-preview">
          {trimmedSlug.length === 0
            ? "No slug yet."
            : `This project will be “${trimmedSlug}”.`}
          {suggested && suggested !== trimmedSlug ? (
            <>
              {" "}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                data-testid="ra-slug-adopt"
                onClick={() => setSlug(suggested)}
              >
                Use “{suggested}”
              </button>
            </>
          ) : null}
        </p>

        <TextField
          label="Brand"
          value={brand}
          data-testid="ra-brand"
          onChange={(e) => setBrand(e.target.value)}
        />
        <TextField
          label="Language"
          hint="Two-letter code, e.g. de or en."
          value={lang}
          data-testid="ra-lang"
          onChange={(e) => setLang(e.target.value)}
        />
        <TextField
          label="Word target"
          hint="Leave empty for no target. Clearing it is sent as an explicit clear."
          inputMode="numeric"
          value={words}
          data-testid="ra-target-words"
          onChange={(e) => setWords(e.target.value)}
        />

        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            data-testid="ra-submit"
            disabled={busy || !changed}
            title={changed ? undefined : "Nothing has changed yet."}
            icon={<PenLine size={15} strokeWidth={1.5} />}
          >
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
